import { useFormik, Form, FormikProvider } from 'formik';
import { Button, Input, Select } from 'styled';
import InputField from 'components/formik/InputField';
import Model from 'components/Modal';
import { getAllData, updateDocumentByID } from 'apis/customApiHandler';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useCallback, useState } from 'react';
import ReactSelect from 'react-select';
import { useSelector } from 'react-redux';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { formSelectStyles } from 'utils/reactSelectStyles';
import { validatePasswordPolicy } from 'utils/passwordPolicy';
import { saveErrorCreationEvent, emailDomain } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from 'utils/analyticsEvents';
import { useLocation } from 'react-router-dom';

const PasswordInput = ({ formik, name, label, visible, onToggleVisible, disabled }) => (
  <div className="flex flex-col justify-center gap-0 py-3 text-gray-600 font-normal">
    <label htmlFor={`edit-user-${name}`}>{label}</label>
    <div className="relative">
      <Input
        id={`edit-user-${name}`}
        type="text"
        name={name}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        disabled={disabled}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-form-type="other"
        className={`!pr-10 ${disabled ? '!bg-gray-50 !shadow-none' : ''}`}
        style={visible ? undefined : { WebkitTextSecurity: 'disc' }}
      />
      <button
        type="button"
        className="absolute inset-y-0 right-3 flex items-center text-gray-600"
        onClick={onToggleVisible}
        disabled={disabled}
        aria-label={`${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
      >
        {visible ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
    {formik.touched[name] && formik.errors[name] ? (
      <p className="text-red-500 font-normal">*{formik.errors[name]}</p>
    ) : null}
  </div>
);

const EditUser = ({ handleClick, data, desable }) => {
  const [isDesable, setIsDesable] = useState(desable);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const currentUser = useSelector((state) => state.user.currentUser);
  const canUpdatePassword = currentUser?.userRole?.name === 'SUPER_ADMIN';
  const location = useLocation();
  const { data: userRoles } = useQuery([`/roles`], () => getAllData('/roles'));
  const { data: allFleets } = useQuery([`/customer-fleet`], () =>
    getAllData('/customer-fleet', {
      fromUsers: 1,
      limit: 3000,
    })
  );
  const { data: allHubs } = useQuery([`/customer-hub`], () =>
    getAllData('/customer-hub', {
      fromUsers: 1,
      limit: 3000,
    })
  );
  const queryClient = useQueryClient();

  function userEditEventFunc(eventName, modelName, params) {
    return saveErrorCreationEvent(eventName, modelName, "Users", location.pathname, params);
  }

  const formik = useFormik({
    initialValues: {
      name: data.name || '',
      email: data.email || '',
      userRole: data?.userRole?._id || '',
      //   pin: '',
      phone: data.phone || '',
      fleets: data.fleets.map((f) => f._id) || [],
      hubs: data.hubs.map((h) => h._id) || [],
      password: '',
      confirmPassword: '',
    },
    onSubmit: async (values) => {
      const { password, confirmPassword, ...details } = values;
      try {
        const availableHubOptions = getHubOptions()?.filter((opt) => opt?.value !== 'all') ?? [];
        const eventParams = {
          edited_user_id: data._id,
          email_domain: emailDomain(values.email),
          role_name: userRoles?.data?.find((item) => item._id === values.userRole)?.name,
          fleets: values?.fleets?.length === allFleets?.data?.length ? "All" : allFleets?.data?.filter((item) => values.fleets.some((fleet) => fleet === item._id))?.map((i) => i.fleetName),
          hubs: values?.hubs?.length === availableHubOptions.length ? "All" : allHubs?.data?.filter((item) => values.hubs.some((hub) => hub === item._id))?.map((i) => i.hubName),
        };
        userEditEventFunc(ANALYTICS_EVENTS.CLICK_EDIT_CHANGES, "Edit User", eventParams);
        const Data = await updateDocumentByID('user', data._id, {
          ...details,
        });
        if (Data) {
          userEditEventFunc(ANALYTICS_EVENTS.SYS_EDIT_USER_SUCCESS, "Edit User", eventParams);
          toast.success('Successfully Updated');
          queryClient.invalidateQueries('users');
          if (canUpdatePassword && password) {
            const passwordUpdated = await updateUserPassword(password);
            if (!passwordUpdated) return;
          }
          handleClick();
        }
      } catch (error) {
        const errMsg = error?.response?.data?.message || error?.message;
        userEditEventFunc(ANALYTICS_EVENTS.SYS_EDIT_USER_ERROR, "Edit User", { errMsg });
        return null;
      }
    },

    validate: (values) => {
      let errors = {};
      let validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email);
      //check if email  is valid
      if (!validEmail) {
        errors.email = 'Invalid Email';
      }
      if (!values.name) errors.name = 'Required';
      if (!values.email) errors.email = 'Required';
      if (!values.userRole) errors.userRole = 'Required';
      if (!values.phone) errors.phone = 'Required';
      if (!values.fleets || !values.fleets.length) errors.fleets = 'Required';
      if (!values.hubs || !values.hubs.length) errors.hubs = 'Required';
      if (canUpdatePassword && (values.password || values.confirmPassword)) {
        const policyError = validatePasswordPolicy(values.password);
        if (!values.password) errors.password = 'Required';
        else if (policyError) errors.password = policyError;
        if (!values.confirmPassword) errors.confirmPassword = 'Required';
        else if (values.confirmPassword !== values.password) errors.confirmPassword = 'Passwords do not match';
      }
      // console.log({
      //   errors,
      // });
      return errors;
    },
  });

  async function updateUserPassword(password) {
    try {
      const response = await updateDocumentByID('user/admin-update-password', data._id, { password });
      if (!response) return false;
      toast.success('Password updated successfully');
      return true;
    } catch (error) {
      return false;
    }
  }

  const getFleetOptions = useCallback(() => {
    if (allFleets && allFleets.data && allFleets.data.length) {
      return [
        {
          label: 'Select All',
          value: 'all',
        },
        ...allFleets?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
          };
        }),
      ];
    }
  }, [allFleets]);

  const getHubOptions = useCallback(() => {
    if (allHubs && allHubs.data && allHubs.data.length) {
      return [
        formik?.values?.fleets?.length && {
          label: 'Select All',
          value: 'all',
        },
        ...(allHubs?.data?.length &&
          allHubs?.data
            ?.filter((hub) => {
              const selectedFleets = allFleets?.data?.filter((fleet) =>
                formik.values.fleets?.includes(fleet._id)
              );

              return selectedFleets?.some(
                (fleet) => fleet.fleetCode === hub.fleetCode
              );
            })
            ?.map((hub) => {
              return {
                value: hub._id,
                label: hub.hubName,
              };
            })),
      ];
    }
  }, [allFleets, allHubs, formik]);

  return (
    <Model
      handleClick={handleClick}
      title={`${desable ? 'View' : 'Edit'} User Details`}
      className="max-w-2xl"
    >
      <FormikProvider value={formik}>
        <Form className="px-5 text-gray-700 font-medium text-base grid  lg:gap-x-14 gap-y-5 grid-flow-row grid-cols-1 lg:grid-cols-2 w-full">
          <InputField
            desable={isDesable}
            type="text"
            label="Full Name"
            name="name"
          />
          <InputField
            desable={isDesable}
            type="text"
            label="Email"
            name="email"
          />
          <div className=" flex flex-col justify-center gap-0 text-gray-600 font-normal">
            <label>User Roles</label>
            <Select
              name="userRole"
              value={formik.values.userRole}
              onChange={formik.handleChange}
              disabled={isDesable ? 'disabled' : ''}
            >
              <option>Select Roles</option>
              {userRoles?.data?.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </Select>
            {formik.touched.userRole && formik.errors.userRole && (
              <p className="text-red-500 font-normal">
                {formik.errors.userRole}
              </p>
            )}
          </div>
          <InputField
            desable={isDesable}
            type="text"
            label="Phone"
            name="phone"
          />

          <div className="flex flex-col gap-0 self-start text-gray-600 font-normal">
            <label className="py-3 flex flex-col gap-2">
              Select Fleets
              <ReactSelect
                styles={formSelectStyles}
                className="shadow-md rounded-md"
                options={getFleetOptions()}
                value={
                  formik.values.fleets &&
                  formik.values.fleets?.map((fleet) => {
                    return {
                      value: fleet,
                      label: allFleets?.data?.find((item) => item._id === fleet)
                        ?.fleetName,
                    };
                  })
                }
                onChange={(value) => {
                  const allValues = value?.map((item) => item.value);
                  if (allValues.includes('all')) {
                    formik.setFieldValue(
                      'fleets',
                      allFleets?.data?.map((item) => {
                        return item._id;
                      })
                    );
                  } else {
                    let selectedFleets = allFleets?.data?.filter((fleet) =>
                      value?.map((item) => item.value)?.includes(fleet._id)
                    );

                    let selectedHubs = allHubs?.data?.filter((hub) =>
                      formik?.values?.hubs?.map((item) => item)?.includes(hub._id)
                    );

                    //remove hubs from selected fleets where
                    formik.setFieldValue(
                      'hubs',
                      selectedHubs
                        ?.filter((hub) =>
                          selectedFleets.find(
                            (fleet) => fleet.fleetCode === hub.fleetCode
                          )
                        )
                        .map((hub) => hub._id)
                    );

                    formik.setFieldValue(
                      'fleets',
                      value.map((fleet) => fleet.value)
                    );
                  }
                }}
                name="fleets"
                isMulti={true}
              />
              {formik.touched.fleets && formik.errors.fleets ? (
                <p className="text-red-500 font-normal">
                  *{formik.errors.fleets}
                </p>
              ) : null}
            </label>
          </div>

          <div className="flex flex-col gap-0 self-start text-gray-600 font-normal">
            <label className="py-3 flex flex-col gap-2">
              Select Hubs
              <ReactSelect
                styles={formSelectStyles}
                className="shadow-md rounded-md"
                options={getHubOptions()}
                value={
                  formik.values.hubs &&
                  formik.values.hubs?.map((hub) => {
                    return {
                      value: hub,
                      label: allHubs?.data?.find((item) => item._id === hub)
                        ?.hubName,
                    };
                  })
                }
                onChange={(value) => {
                  const allValues = value?.map((item) => item.value);

                  if (allValues.includes('all')) {
                    formik.setFieldValue(
                      'hubs',
                      allHubs?.data
                        ?.filter((hub) => {
                          const selectedFleets = allFleets?.data?.filter(
                            (fleet) => formik.values.fleets?.includes(fleet._id)
                          );

                          return selectedFleets?.some(
                            (fleet) => fleet.fleetCode === hub.fleetCode
                          );
                        })
                        ?.map((hub) => hub._id)
                    );
                  } else {
                    formik.setFieldValue(
                      'hubs',
                      value.map((hub) => hub.value)
                    );
                  }
                }}
                name="hubs"
                isMulti={true}
              />
              {formik.touched.hubs && formik.errors.hubs ? (
                <p className="text-red-500 font-normal">*{formik.errors.hubs}</p>
              ) : null}
            </label>
          </div>

          {canUpdatePassword && (
            <>
              <div className="lg:col-span-2 border-t border-gray-200 pt-4 text-gray-600 font-normal">
                <p className="font-medium text-gray-700">Update Password</p>
                <p className="text-xs">
                  Leave blank to keep the current password. A new password needs at least 12 characters with 3 of:
                  uppercase, lowercase, number, symbol.
                </p>
              </div>
              <PasswordInput
                formik={formik}
                name="password"
                label="New Password"
                visible={showPassword}
                onToggleVisible={() => setShowPassword((show) => !show)}
                disabled={isDesable}
              />
              <PasswordInput
                formik={formik}
                name="confirmPassword"
                label="Confirm Password"
                visible={showConfirmPassword}
                onToggleVisible={() => setShowConfirmPassword((show) => !show)}
                disabled={isDesable}
              />
            </>
          )}
        </Form>
        {isDesable ? (
          <div className="px-5 flex justify-end items-center gap-4 pb-8 flex-wrap sm:w-full">
            <Button bgSecondary onClick={handleClick}>
              Cancel
            </Button>
            <Button type="button" bgPrimary onClick={() => setIsDesable(false)}>
              Edit
            </Button>
          </div>
        ) : (
          <div className="px-5 flex justify-end items-center gap-4 pb-8 flex-wrap sm:w-full">
            <Button bgSecondary onClick={handleClick}>
              Cancel
            </Button>
            <Button type="button" bgPrimary onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
              Save Changes
            </Button>
          </div>
        )}
      </FormikProvider>
    </Model>
  );
};

export default EditUser;
